/*
 * nb-mega-menu.js
 *
 * Open/close behavior for the Nabema mega-menu panels rendered inside
 * `.nb-header__nav-item[data-nb-has-submenu]`.
 *
 * - Hover on desktop (delayed close so the cursor can cross the gap)
 * - Click toggle on touch / keyboard
 * - Escape closes
 * - Outside-click closes
 * - Focus-trap-light: arrow keys move between sibling items
 */
(function () {
  const HOVER_OPEN_DELAY = 80;
  const HOVER_CLOSE_DELAY = 200;

  const items = document.querySelectorAll('.nb-header__nav-wrap[data-nb-has-submenu]');
  if (!items.length) return;

  const state = new WeakMap();
  let openItem = null;
  let openTimer;

  function trigger(item) {
    return item.querySelector('.nb-header__nav-item');
  }
  function open(item) {
    if (openItem && openItem !== item) close(openItem, true);
    item.setAttribute('data-nb-open', '');
    const t = trigger(item);
    if (t) t.setAttribute('aria-expanded', 'true');
    openItem = item;
  }
  function close(item, immediate) {
    if (!item) return;
    const finish = function () {
      item.removeAttribute('data-nb-open');
      const t = trigger(item);
      if (t) t.setAttribute('aria-expanded', 'false');
      if (openItem === item) openItem = null;
    };
    if (immediate) {
      finish();
    } else {
      clearTimeout(state.get(item)?.closeTimer);
      state.set(item, { closeTimer: setTimeout(finish, HOVER_CLOSE_DELAY) });
    }
  }

  items.forEach(function (item) {
    // Announce the submenu to assistive tech (progressive enhancement).
    const trig = trigger(item);
    if (trig) {
      trig.setAttribute('aria-haspopup', 'true');
      trig.setAttribute('aria-expanded', 'false');
    }
    // Hover (desktop)
    item.addEventListener('mouseenter', function () {
      clearTimeout(openTimer);
      clearTimeout(state.get(item)?.closeTimer);
      openTimer = setTimeout(function () {
        open(item);
      }, HOVER_OPEN_DELAY);
    });
    item.addEventListener('mouseleave', function () {
      clearTimeout(openTimer);
      close(item);
    });

    // Click toggle (touch + keyboard activation)
    item.addEventListener('click', function (e) {
      // If it's a real link with a destination AND already open, let it navigate.
      // First click opens; second click navigates.
      const isOpen = item.hasAttribute('data-nb-open');
      if (!isOpen) {
        e.preventDefault();
        open(item);
      }
    });

    // Keyboard
    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        close(item, true);
        item.focus();
      } else if (e.key === 'ArrowDown' && item.hasAttribute('data-nb-open')) {
        const firstLink = item.querySelector('[data-nb-mega-panel] a');
        if (firstLink) {
          e.preventDefault();
          firstLink.focus();
        }
      }
    });
  });

  // Outside click closes
  document.addEventListener('click', function (e) {
    if (!openItem) return;
    if (!openItem.contains(e.target)) close(openItem, true);
  });

  // Escape from inside a panel
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openItem) {
      const parent = openItem;
      close(openItem, true);
      parent.focus();
    }
  });
})();

/*
 * Mobile drawer — open/close behavior for .nb-drawer.
 * Triggered by [data-nb-drawer-open] (hamburger).
 * Dismissed by [data-nb-drawer-close] (backdrop + close button) or Escape.
 * Locks body scroll while open.
 */
(function () {
  const drawer = document.querySelector('[data-nb-drawer]');
  if (!drawer) return;
  const openBtns = document.querySelectorAll('[data-nb-drawer-open]');
  const closeBtns = document.querySelectorAll('[data-nb-drawer-close]');
  let lastFocused = null;

  function openDrawer() {
    lastFocused = document.activeElement;
    drawer.setAttribute('data-nb-open', '');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    openBtns.forEach(function (b) {
      b.setAttribute('aria-expanded', 'true');
    });
    // Move focus into the panel (close button is first interactive)
    const closeBtn = drawer.querySelector('.nb-drawer__close');
    if (closeBtn) closeBtn.focus();
  }
  function closeDrawer() {
    drawer.removeAttribute('data-nb-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    openBtns.forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  openBtns.forEach(function (btn) {
    btn.addEventListener('click', openDrawer);
  });
  closeBtns.forEach(function (btn) {
    btn.addEventListener('click', closeDrawer);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.hasAttribute('data-nb-open')) {
      closeDrawer();
    }
  });

  // Focus trap — keep Tab/Shift+Tab inside the open drawer.
  drawer.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !drawer.hasAttribute('data-nb-open')) return;
    const focusables = Array.prototype.filter.call(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
      function (el) { return el.offsetParent !== null; }
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
})();

/*
 * Country/currency picker — close the <details> popover when clicking
 * outside or pressing Escape.
 */
(function () {
  const locale = document.querySelector('[data-nb-locale]');
  if (!locale) return;
  document.addEventListener('click', function (e) {
    if (locale.open && !locale.contains(e.target)) locale.open = false;
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && locale.open) locale.open = false;
  });
})();

/*
 * Predictive search — debounced query against /search/suggest.json.
 * Renders product results with thumbnail, vendor, price, plus a footer
 * link that bridges to ninebind for the same query.
 */
(function () {
  const wraps = document.querySelectorAll('[data-nb-search]');
  if (!wraps.length) return;

  const DEBOUNCE_MS = 200;
  const MIN_CHARS = 2;
  const LIMIT = 6;

  function money(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  wraps.forEach(function (wrap) {
    const input = wrap.querySelector('[data-nb-search-input]');
    const out = wrap.querySelector('[data-nb-search-results]');
    if (!input || !out) return;

    let timer = null;
    let lastQuery = '';
    let activeIndex = -1;

    function clearActive() {
      out.querySelectorAll('[data-nb-active]').forEach(function (el) {
        el.removeAttribute('data-nb-active');
      });
      input.removeAttribute('aria-activedescendant');
    }
    function setActiveByIndex(idx) {
      const items = out.querySelectorAll('.nb-suggest__item, .nb-suggest__bridge');
      if (!items.length) return;
      activeIndex = ((idx % items.length) + items.length) % items.length;
      clearActive();
      const el = items[activeIndex];
      if (!el.id) el.id = 'nb-suggest-opt-' + activeIndex;
      el.setAttribute('data-nb-active', '');
      input.setAttribute('aria-activedescendant', el.id); // announce the highlighted option to SR users
      el.scrollIntoView({ block: 'nearest' });
    }

    function render(q, data) {
      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      const pages = (data && data.resources && data.resources.results && data.resources.results.pages) || [];

      const parts = [];
      if (products.length) {
        parts.push('<div class="nb-suggest__group"><div class="nb-suggest__heading">Products</div>');
        products.forEach(function (p) {
          // /search/suggest.json returns featured_image as { url, width, height, aspect_ratio };
          // image is a plain URL string. Prefer .url; fall back to image.
          const imgUrl = (p.featured_image && p.featured_image.url) || p.image || '';
          const img = imgUrl
            ? '<div class="nb-suggest__img"><img src="' + escapeHtml(imgUrl) + '" alt="" loading="lazy"></div>'
            : '<div class="nb-suggest__img"></div>';
          const vendor = p.vendor ? '<div class="nb-suggest__meta">' + escapeHtml(p.vendor) + '</div>' : '';
          const price = typeof p.price === 'number'
            ? '<div class="nb-suggest__price">' + money(p.price) + '</div>'
            : '';
          parts.push(
            '<a class="nb-suggest__item" href="' + escapeHtml(p.url) + '" role="option">'
              + img
              + '<div class="nb-suggest__main">'
              + '<div class="nb-suggest__title">' + escapeHtml(p.title) + '</div>'
              + vendor
              + '</div>'
              + price
              + '</a>'
          );
        });
        parts.push('</div>');
      }
      if (pages.length) {
        parts.push('<div class="nb-suggest__group"><div class="nb-suggest__heading">Pages</div>');
        pages.forEach(function (pg) {
          parts.push(
            '<a class="nb-suggest__item" href="' + escapeHtml(pg.url) + '" role="option">'
              + '<div class="nb-suggest__main">'
              + '<div class="nb-suggest__title">' + escapeHtml(pg.title) + '</div>'
              + '</div>'
              + '</a>'
          );
        });
        parts.push('</div>');
      }

      if (!parts.length) {
        parts.push('<div class="nb-suggest__empty">No products match "' + escapeHtml(q) + '"</div>');
      }

      // Always offer the ninebind singles bridge at the bottom
      parts.push(
        '<a class="nb-suggest__bridge" href="/apps/ninebind/search">★ Browse the full singles catalog →</a>'
      );

      out.innerHTML = parts.join('');
      out.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      activeIndex = -1;
    }

    function hide() {
      out.hidden = true;
      out.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function query(q) {
      if (q === lastQuery) return;
      lastQuery = q;
      if (q.length < MIN_CHARS) {
        hide();
        return;
      }
      const url = '/search/suggest.json?q=' + encodeURIComponent(q)
        + '&resources[type]=product,page'
        + '&resources[limit]=' + LIMIT
        + '&resources[limit_scope]=each';
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          render(q, data);
        })
        .catch(function () { /* silent */ });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        query(input.value.trim());
      }, DEBOUNCE_MS);
    });
    input.addEventListener('focus', function () {
      if (out.innerHTML && input.value.trim().length >= MIN_CHARS) {
        out.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveByIndex(activeIndex + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveByIndex(activeIndex - 1);
      } else if (e.key === 'Enter') {
        const items = out.querySelectorAll('.nb-suggest__item, .nb-suggest__bridge');
        if (activeIndex >= 0 && items[activeIndex]) {
          e.preventDefault();
          window.location.href = items[activeIndex].href;
        }
      } else if (e.key === 'Escape') {
        hide();
      }
    });

    // Click outside closes
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) hide();
    });
  });
})();
